// components/ChartSection.tsx
import { useState, useMemo, memo, useCallback, useRef } from "react";
import RadarChart from "../../../../Dashboard/components/RadarChart";
import { PrincipleChartData } from "../../core/_models";
import CombinedBarChart from "./CombinedBarChart";
import DonutChart from "./DonutChart";
import { useAuth } from "../../../../auth";
import ChartExportMenu from "../../../../charts/ChartExportMenu";

interface ChartSectionProps {
    principleChartData: PrincipleChartData[];
    subPrincipleWeights: Record<string, number>;
    principleColors: Record<string, string>;
    loading: boolean;
    projectId: number;
}

type ChartTab = "subPrinciple" | "evaluator";

const chartTabs: readonly { id: ChartTab; label: string }[] = [
    {
        id: "subPrinciple",
        label: "Sub Principle Wise",
    },
    {
        id: "evaluator",
        label: "Evaluator Wise",
    },
] as const;

// Memoized loading component
const ChartLoading = memo(() => (
    <>
        <div className="card p-4 shadow-sm mb-2">
            <div className="d-flex align-items-center justify-content-between">
                <p className="mb-0 fw-bold text-gray-700 fs-6">
                    Loading Charts
                </p>

                <div
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                >
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        </div>

        <div className="card p-4 shadow-sm">
            <div className="d-flex justify-content-center align-items-center h-400px">
                <div className="text-center">
                    <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                    >
                        <span className="visually-hidden">
                            Loading chart data...
                        </span>
                    </div>

                    <p className="text-gray-500">Loading chart data...</p>
                </div>
            </div>
        </div>
    </>
));

ChartLoading.displayName = 'ChartLoading';

// Memoized no data component
const ChartNoData = memo(() => (
    <>
        <div className="card p-4 shadow-sm mb-2">
            <p className="mb-0 fw-bold text-gray-700 fs-6">
                No Data Available
            </p>
        </div>

        <div className="card p-4 shadow-sm">
            <div className="d-flex justify-content-center align-items-center h-400px">
                <div className="text-center">
                    <p className="text-gray-500 mb-0">
                        No data available
                    </p>

                    <p className="text-gray-400 fs-8">
                        Please try again later
                    </p>
                </div>
            </div>
        </div>
    </>
));

ChartNoData.displayName = 'ChartNoData';

// Helper function to get chart data based on active tab
const getChartDataForPrinciple = (
    principle: PrincipleChartData,
    activeChartTab: ChartTab
) => {
    if (activeChartTab === "subPrinciple") {
        return principle.chart_data_sp ?? [];
    }

    if (activeChartTab === "evaluator") {
        return principle.chart_data_eval ?? [];
    }

    // Exhaustive check - this should never happen with proper TypeScript
    const exhaustiveCheck: never = activeChartTab;
    console.warn(`Unknown chart tab: ${exhaustiveCheck}`);
    return [];
};

// Individual Radar Chart card component
interface RadarChartCardProps {
    principle: PrincipleChartData;
    activeChartTab: ChartTab;
}

const RadarChartCard = memo(({ principle, activeChartTab }: RadarChartCardProps) => {
    const chartData = useMemo(() => {
        return getChartDataForPrinciple(principle, activeChartTab);
    }, [principle, activeChartTab]);

    const hasChartData = chartData.length > 0;

    return (
        <div className="col-12 col-md-6 col-lg-3">
            <div className="card p-3 shadow-sm h-100">
                <h6 className="fw-bold text-gray-700 mb-2 fs-6">
                    {principle.name}
                </h6>

                <div className="h-100 w-100">
                    {hasChartData ? (
                        <RadarChart
                            chart_data={chartData}
                            chartHeight={300}
                        />
                    ) : (
                        <div className="d-flex justify-content-center align-items-center h-400px">
                            <div className="text-center">
                                <p className="text-gray-500 mb-0">
                                    No chart data
                                </p>
                                <p className="text-gray-400 fs-8">
                                    No data found
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

RadarChartCard.displayName = 'RadarChartCard';

// Admin charts grid component
interface AdminChartsGridProps {
    principleChartData: PrincipleChartData[];
    activeChartTab: ChartTab;
    gridRef: React.RefObject<HTMLDivElement | null>;
}

const AdminChartsGrid = memo(({ principleChartData, activeChartTab, gridRef }: AdminChartsGridProps) => {
    return (
        <>
            <div className="row g-3" ref={gridRef}>
                {principleChartData.map((principle) => (
                    <RadarChartCard
                        key={principle.name}
                        principle={principle}
                        activeChartTab={activeChartTab}
                    />
                ))}
            </div>
        </>
    );
});

AdminChartsGrid.displayName = 'AdminChartsGrid';

// Non-admin charts component
interface NonAdminChartsProps {
    principleChartData: PrincipleChartData[];
    principleColors: Record<string, string>;
    subPrincipleWeights: Record<string, number>;
}

const NonAdminCharts = memo(({
    principleChartData,
    principleColors,
    subPrincipleWeights
}: NonAdminChartsProps) => {
    const charts = useMemo(() => (
        <div className="row g-3">
            <div className="col-12 col-md-8">
                <div className="card p-4 shadow-sm h-100">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold text-gray-700 mb-0 fs-6">
                            Sub principle wise
                        </h6>
                    </div>

                    <CombinedBarChart
                        principleChartData={principleChartData}
                        principleColors={principleColors}
                        subPrincipleWeights={subPrincipleWeights}
                        chartHeight={400}
                    />
                </div>
            </div>
            <div className="col-12 col-md-4">
                <div className="card p-4 shadow-sm h-100">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="fw-bold text-gray-700 mb-0 fs-6">
                            Principle wise
                        </h6>
                    </div>
                    <DonutChart
                        principleChartData={principleChartData}
                        principleColors={principleColors}
                        chartHeight={400}
                    />
                </div>
            </div>
        </div>
    ), [principleChartData, principleColors, subPrincipleWeights]);

    return charts;
});

NonAdminCharts.displayName = 'NonAdminCharts';

// Tabs component
interface ChartTabsProps {
    activeChartTab: ChartTab;
    onTabChange: (tabId: ChartTab) => void;
    loading: boolean;
}

const ChartTabs = memo(({ activeChartTab, onTabChange, loading }: ChartTabsProps) => {
    const tabs = useMemo(() => (
        <div className="card p-4 shadow-sm mb-2">
            <div className="d-flex align-items-center gap-3">
                {chartTabs.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        className={`bg-transparent border-0 fs-7 fw-semibold cursor-pointer  ${activeChartTab === tab.id
                            ? 'text-primary border-2 border-primary border-bottom'
                            : 'text-gray-800'
                            }`}
                        onClick={() => onTabChange(tab.id)}
                        disabled={loading}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        </div>
    ), [activeChartTab, onTabChange, loading]);

    return tabs;
});

ChartTabs.displayName = 'ChartTabs';

// Main ChartSection component
export const ChartSection = ({
    principleChartData,
    subPrincipleWeights,
    principleColors,
    loading,
    projectId,
}: ChartSectionProps) => {
    const [activeChartTab, setActiveChartTab] = useState<ChartTab>("subPrinciple");
    const gridRef = useRef<HTMLDivElement>(null);
    const { auth } = useAuth();

    const isAdmin = auth?.isAdmin === 1;

    const handleTabChange = useCallback((tabId: ChartTab) => {
        setActiveChartTab(tabId);
    }, []);

    if (loading) {
        return <ChartLoading />;
    }

    if (principleChartData.length === 0) {
        return <ChartNoData />;
    }
    const tabLabel = chartTabs.find((tab) => tab.id === activeChartTab)?.label ?? "Chart";
    const canExport = principleChartData.some(
        (principle) => getChartDataForPrinciple(principle, activeChartTab).length > 0,
    );
    return (
        <>
            {isAdmin && (
                <div className="position-relative">
                <ChartTabs
                    activeChartTab={activeChartTab}
                    onTabChange={handleTabChange}
                    loading={loading}
                />
                <ChartExportMenu
                    fileName={`${projectId}-${tabLabel}`}
                    disabled={!canExport}
                    getElement={() => gridRef.current}
                    className="export-menu-absolute"
                />
                </div>
            )}

            {isAdmin ? (
                <AdminChartsGrid
                    principleChartData={principleChartData}
                    activeChartTab={activeChartTab}
                    gridRef={gridRef}
                />
            ) : (
                <NonAdminCharts
                    principleChartData={principleChartData}
                    principleColors={principleColors}
                    subPrincipleWeights={subPrincipleWeights}
                />
            )}
        </>
    );
};